import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Badge,
  Modal,
  notification,
  Breadcrumb,
  Alert,
  Input,
  Form,
  Descriptions
} from "antd";
import {
  FaCheck,
  FaTools,
  FaSyncAlt,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes
} from "react-icons/fa";
import "./MaintenancePage.css";

const { Text } = Typography;
const { TextArea } = Input;

const MaintenancePage = () => {
  const { authData } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);

  const breadcrumbItems = [
    {
      title: (
        <Link to={authData?.role?.toLowerCase() === "systemadmin" ? "/admin/dashboard" : "/hotels/dashboard"}>
          Home
        </Link>
      ),
      key: "home"
    },
    {
      title: "My Maintenance Tasks",
      key: "maintenance"
    }
  ];

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/Maintenance/MyTasks?hotelId=${authData.hotelId}&userIdOrEmail=${authData.userId || authData.userName}`
      );
      const tasksData = Array.isArray(response.data) ? response.data : [];
      setTasks(
        tasksData.map((task) => ({
          ...task,
          key: task.maintenanceTaskId,
          assignedTo: task.assignedTo || {
            userId: task.assignedToUserId,
            userName: task.assignedUserName || "Unknown"
          }
        }))
      );
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors({
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authData?.hotelId && (authData?.userId || authData?.userName)) {
      fetchTasks();
    }
  }, [authData?.hotelId, authData?.userId, authData?.userName]);

  const renderStatusTag = (status) => {
    const statusConfig = {
      Pending: { color: "orange", icon: <FaClock /> },
      InProgress: { color: "blue", icon: <FaTools /> },
      Completed: { color: "green", icon: <FaCheckCircle /> },
      Cancelled: { color: "red", icon: <FaTimes /> },
      Unknown: { color: "gray" }
    };
    const config = statusConfig[status] || statusConfig.Unknown;
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const renderPriorityTag = (priority) => {
    const priorityConfig = {
      Low: { color: "green" },
      Medium: { color: "blue" },
      High: { color: "orange", icon: <FaExclamationTriangle /> },
      Emergency: { color: "red", icon: <FaExclamationTriangle /> },
      Unknown: { color: "gray" }
    };
    const config = priorityConfig[priority] || priorityConfig.Unknown;
    return (
      <Tag color={config.color} icon={config.icon}>
        {priority}
      </Tag>
    );
  };

  const handleCompleteTask = async () => {
  try {
    const values = await form.validateFields();
    const updatePayload = {
      status: "Completed",
      description: values.notes || currentTask.description,
      completedDate: new Date().toISOString(),
      title: currentTask.title,
      roomId: currentTask.roomId,
      location: currentTask.location,
      priority: currentTask.priority,
      assignmentNotes: currentTask.assignmentNotes,
      hotelId: currentTask.hotelId,
      assignedToUserId: currentTask.assignedToUserId ? parseInt(currentTask.assignedToUserId) : null
    };

    await axiosInstance.put(
      `/Maintenance/${currentTask.maintenanceTaskId}?hotelId=${currentTask.hotelId}`,
      updatePayload
    );

    notification.success({
      message: "Success",
      description: "Task marked as completed"
    });

    setCompleteModalOpen(false);
    setApiErrors(null);
    fetchTasks();
  } catch (err) {
    console.error("Error completing task:", err);
    setApiErrors({
      status: err.response?.status,
      data: err.response?.data,
      config: err.config
    });
    notification.error({
      message: "Error completing task",
      description:
        err.response?.data?.title ||
        err.response?.data?.message ||
        "Please check your input and try again"
    });
  }
};


  const columns = [
    {
      title: "Task ID",
      dataIndex: "maintenanceTaskId",
      key: "maintenanceTaskId",
      width: 100
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            setCurrentTask(record);
            setViewModalOpen(true);
          }}
        >
          {text}
        </Button>
      )
    },
      {
        title: "Room",
        key: "room",
        render: (_, record) => {
            // First try to show room number if available
            if (record.roomNumber) {
                return record.roomNumber;
            }
            // If no room number but has room ID, show that
            if (record.roomId) {
                return `Room ID: ${record.roomId}`;
            }
            // For location-only requests, show the location
            return <Tag color="blue">{record.location}</Tag>;
        }
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text) => text || "N/A"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatusTag
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: renderPriorityTag
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space key={`action-${record.maintenanceTaskId}`}>
          <Button
            type="primary"
            icon={<FaCheck />}
            onClick={() => {
              setCurrentTask(record);
              setCompleteModalOpen(true);
            }}
            disabled={record.status === "Completed"}
          >
            Complete
          </Button>
        </Space>
      )
    }
  ];
//className="maintenance-page"
  return (
    <div >
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <Text strong>My Assigned Maintenance Tasks</Text>
            <Badge count={tasks.length} showZero style={{ backgroundColor: "#1890ff" }} />
          </Space>
        }
        extra={
          <Button icon={<FaSyncAlt />} onClick={fetchTasks} loading={loading}>
            Refresh
          </Button>
        }
      >
        {tasks.length === 0 && !loading ? (
          <Alert
            message="No Tasks Assigned"
            description="You currently have no maintenance tasks assigned to you."
            type="info"
            showIcon
          />
        ) : (
          <Table
            rowKey="maintenanceTaskId"
            columns={columns}
            dataSource={tasks}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total) => `Total ${total} tasks`
            }}
            scroll={{ x: true }}
          />
        )}

        <Modal
          title={`Complete Task #${currentTask?.maintenanceTaskId || ""}`}
          open={completeModalOpen}
          onCancel={() => setCompleteModalOpen(false)}
          onOk={handleCompleteTask}
          okText="Mark as Completed"
        >
         
         {apiErrors && (
            <Alert
              message="Validation Error"
              description={
                typeof apiErrors?.data?.errors === "object"
                  ? (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {Object.entries(apiErrors.data.errors).map(([field, messages]) => (
                          <li key={field}>
                            <strong>{field}:</strong> {Array.isArray(messages) ? messages.join(', ') : messages}
                          </li>
                        ))}
                      </ul>
                    )
                  : apiErrors?.data?.errors || JSON.stringify(apiErrors?.data)
              }
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}



          <Form form={form} layout="vertical">
            <Form.Item
              name="notes"
              label="Completion Notes"
              rules={[{ required: true, message: "Please enter completion notes" }]}
            >
              <TextArea rows={4} placeholder="Describe the work completed" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={`Task Details #${currentTask?.maintenanceTaskId || ""}`}
          open={viewModalOpen}
          onCancel={() => setViewModalOpen(false)}
          footer={null}
          width={700}
        >
          {currentTask && (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Title">{currentTask.title}</Descriptions.Item>
              <Descriptions.Item label="Description">{currentTask.description || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Status">{renderStatusTag(currentTask.status)}</Descriptions.Item>
              <Descriptions.Item label="Priority">{renderPriorityTag(currentTask.priority)}</Descriptions.Item>
              <Descriptions.Item label="Room">{currentTask.roomId || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Location">{currentTask.location || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {currentTask.assignedTo
                  ? `${currentTask.assignedTo.userName} (${currentTask.assignedTo.role || "N/A"})`
                  : "Not assigned"}
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {new Date(currentTask.createdDate).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Completed Date">
                {currentTask.completedDate ? new Date(currentTask.completedDate).toLocaleString() : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Assignment Notes">
                {currentTask.assignmentNotes || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default MaintenancePage;
